// -----------------------------------------------------------------------------
// Internal Chat page — thin orchestrator.
//
// Slack-style two-pane layout: the left pane (`ChannelSidebar`) lists
// channels and DMs, the right pane (`ChannelThread`) shows the active
// conversation. This file owns the data hooks, selection state, SignalR
// event wiring and modal toggles - the actual UI is split across the
// sibling components in this folder.
//
// Backed by `useChat` (REST against `/chat/*`) and the `chatHub` SignalR
// connection exposed via `SignalRContext`. The hub is responsible for
// pushing live message + reaction + presence + membership events; on
// each event we invalidate the relevant React Query cache key and the
// affected component re-fetches.
// -----------------------------------------------------------------------------

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useSignalR } from "../../contexts/SignalRContext";
import useChat, { chatQueryKeys } from "../../data/useChat";
import { ACTIVE_CHANNEL_STORAGE_KEY } from "../../hooks/useChatDesktopNotifications";
import {
  useChatTypingIndicator,
  useSendTypingSignal,
} from "../../hooks/useChatTyping";
import { channelLabel } from "../../utils/chatHelpers";
import { IChatChannel, IChatMessage, IChatUser } from "../../models/Chat";

import ChannelSidebar from "./ChannelSidebar";
import ChannelThread from "./ChannelThread";
import ManageMembersModal from "./modals/ManageMembersModal";
import NewChannelModal from "./modals/NewChannelModal";
import NewDmModal from "./modals/NewDmModal";

export default function Chat() {
  const { setInfo } = usePageNameContext();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { chatConnection, chatPresence } = useSignalR();

  const meId = auth.loggedIn ? auth.userId : null;

  const chat = useChat();
  const usersQuery = chat.users.data?.data ?? [];
  const channelsQuery = chat.channels.data?.data ?? [];

  useEffect(() => {
    setInfo({
      name: "Internal chat",
      desc: "Talk to your team in real time — channels and direct messages.",
    });
  }, []);

  // Decorate users with presence + isMe so the rest of the page can rely
  // on the existing IChatUser shape.
  const users: IChatUser[] = useMemo(() => {
    return usersQuery.map((u) => ({
      ...u,
      presence: chatPresence[u.id] ?? u.presence ?? "offline",
      isMe: u.id === meId,
    }));
  }, [usersQuery, chatPresence, meId]);

  const me = users.find((u) => u.isMe) ?? null;

  const usersById = useMemo(() => {
    const map = new Map<string, IChatUser>();
    users.forEach((u) => map.set(u.id, u));
    return map;
  }, [users]);

  // Sort: most recent activity first. Channels with no traffic fall back
  // to their creation timestamp so brand-new rooms still surface.
  const sortedChannels = useMemo(() => {
    return [...channelsQuery].sort((a, b) => {
      const aTime = a.lastMessageAt
        ? Date.parse(a.lastMessageAt)
        : Date.parse(a.createdAt);
      const bTime = b.lastMessageAt
        ? Date.parse(b.lastMessageAt)
        : Date.parse(b.createdAt);
      return bTime - aTime;
    });
  }, [channelsQuery]);

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedChannelId = searchParams.get("channel");

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Honour a `?channel=...` deep link (e.g. from the topbar
  // notifications drawer). We only auto-jump while the requested
  // channel is actually in the user's channel list, then strip the
  // param so refreshing or navigating later doesn't keep dragging the
  // user back to the same conversation.
  useEffect(() => {
    if (!requestedChannelId) return;
    if (sortedChannels.length === 0) return;
    const found = sortedChannels.find((c) => c.id === requestedChannelId);
    if (!found) return;
    setSelectedId(found.id);
    setSearchParams({}, { replace: true });
  }, [requestedChannelId, sortedChannels, setSearchParams]);

  useEffect(() => {
    if (selectedId) return;
    if (requestedChannelId) return;
    const first = sortedChannels.find((c) => c.kind === "channel");
    if (first) setSelectedId(first.id);
  }, [selectedId, sortedChannels, requestedChannelId]);

  const selected = useMemo(
    () => channelsQuery.find((c) => c.id === selectedId) ?? null,
    [channelsQuery, selectedId]
  );

  // Surface the active channel id to the global desktop-notifications
  // hook so it can suppress notifications for the channel the user is
  // actively reading. sessionStorage rather than a context to avoid
  // having to thread a provider through every layout.
  useEffect(() => {
    if (selectedId) {
      sessionStorage.setItem(ACTIVE_CHANNEL_STORAGE_KEY, selectedId);
    } else {
      sessionStorage.removeItem(ACTIVE_CHANNEL_STORAGE_KEY);
    }
    return () => {
      sessionStorage.removeItem(ACTIVE_CHANNEL_STORAGE_KEY);
    };
  }, [selectedId]);

  const messagesQuery = chat.useMessages(selectedId);
  const messages = messagesQuery.messages;

  // Reply target for the composer - cleared on send, cancel, or when
  // switching channels.
  const [replyTo, setReplyTo] = useState<IChatMessage | null>(null);
  useEffect(() => {
    setReplyTo(null);
  }, [selectedId]);

  const typingUserIds = useChatTypingIndicator(selectedId, meId);
  const sendTypingSignal = useSendTypingSignal(selectedId, meId);

  // Keep our presence as "online" while the page is mounted: the hub
  // will already mark us online on connect, but the explicit Identify
  // is what joins us to all our channel groups so DMs and channels
  // stream live.
  useEffect(() => {
    if (!chatConnection || !meId) return;
    chatConnection.invoke("Identify", meId).catch((err) => {
      console.warn("[chat] identify failed", err);
    });
  }, [chatConnection, meId]);

  // Fan SignalR events into React Query invalidations. We only need
  // to invalidate the relevant slice of cache - components re-fetching
  // is cheap and keeps the optimistic mutation paths simple.
  useEffect(() => {
    if (!chatConnection) return;

    const onMessage = (message: IChatMessage) => {
      queryClient.invalidateQueries(chatQueryKeys.messages(message.channelId));
      queryClient.invalidateQueries(chatQueryKeys.channels);
    };

    const onMessageEdited = (message: IChatMessage) => {
      queryClient.invalidateQueries(chatQueryKeys.messages(message.channelId));
    };

    const onReaction = (message: IChatMessage) => {
      queryClient.invalidateQueries(chatQueryKeys.messages(message.channelId));
    };

    const onChannel = () => {
      queryClient.invalidateQueries(chatQueryKeys.channels);
    };

    // If the server tells us we've been removed from a channel, drop
    // it out of the cache immediately and clear the selection if it
    // was the one we were viewing - otherwise the user is left
    // staring at a thread they no longer have access to.
    const onChannelRemoved = (channelId: string) => {
      queryClient.setQueryData(chatQueryKeys.channels, (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.filter((c: IChatChannel) => c.id !== channelId),
        };
      });
      queryClient.removeQueries(chatQueryKeys.messages(channelId));
      setSelectedId((current) => (current === channelId ? null : current));
    };

    chatConnection.on("MessageReceived", onMessage);
    chatConnection.on("MessageEdited", onMessageEdited);
    chatConnection.on("ReactionUpdated", onReaction);
    chatConnection.on("ChannelCreated", onChannel);
    chatConnection.on("ChannelUpdated", onChannel);
    chatConnection.on("ChannelRemoved", onChannelRemoved);

    return () => {
      chatConnection.off("MessageReceived", onMessage);
      chatConnection.off("MessageEdited", onMessageEdited);
      chatConnection.off("ReactionUpdated", onReaction);
      chatConnection.off("ChannelCreated", onChannel);
      chatConnection.off("ChannelUpdated", onChannel);
      chatConnection.off("ChannelRemoved", onChannelRemoved);
    };
  }, [chatConnection, queryClient]);

  // Mark as read whenever the selected channel changes or new messages
  // arrive for it. Small delay keeps React from thrashing writes during
  // the initial render burst.
  const markReadMutation = chat.markRead;
  useEffect(() => {
    if (!selected) return;
    const handle = window.setTimeout(() => {
      markReadMutation.mutate(selected.id);
    }, 200);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, messages.length]);

  // Unread counts are computed server-side and shipped on each channel,
  // so the sidebar can render badges without having to load every
  // channel's messages. Muted channels still show their per-row count
  // but are excluded from the global total below.
  const unreadCount = (channelId: string): number => {
    const channel = sortedChannels.find((c) => c.id === channelId);
    return channel?.unreadCount ?? 0;
  };

  const totalUnread = useMemo(
    () =>
      sortedChannels
        .filter((c) => !c.muted)
        .reduce((sum, c) => sum + (c.unreadCount ?? 0), 0),
    [sortedChannels]
  );

  const [search, setSearch] = useState("");
  const filteredChannels = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedChannels;
    return sortedChannels.filter((c) => {
      const label = channelLabel(c, me?.id ?? null, usersById).toLowerCase();
      const desc = (c.description ?? "").toLowerCase();
      return label.includes(term) || desc.includes(term);
    });
  }, [sortedChannels, search, me, usersById]);

  const teamChannels = filteredChannels.filter((c) => c.kind === "channel");
  const dmChannels = filteredChannels.filter((c) => c.kind === "dm");

  const [showNewChannel, setShowNewChannel] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);
  const [manageMembersChannelId, setManageMembersChannelId] = useState<
    string | null
  >(null);
  const manageMembersChannel = useMemo(
    () =>
      manageMembersChannelId
        ? sortedChannels.find((c) => c.id === manageMembersChannelId) ?? null
        : null,
    [manageMembersChannelId, sortedChannels]
  );

  const handleSend = (body: string, files: File[]) => {
    if (!selected) return;
    const replyToId = replyTo?.id;
    setReplyTo(null);
    // Plain-text sends keep the lightweight JSON path; anything with
    // files goes through the multipart endpoint.
    if (files.length > 0) {
      chat.sendMessageWithAttachments.mutate({
        channelId: selected.id,
        body: { body, replyToId },
        files,
      });
    } else {
      chat.sendMessage.mutate({
        channelId: selected.id,
        body: { body, replyToId },
      });
    }
  };

  const handleEdit = (messageId: string, body: string) => {
    chat.editMessage.mutate({ messageId, body });
  };

  const handleReact = (messageId: string, emoji: string) => {
    chat.toggleReaction.mutate({ messageId, emoji });
  };

  const handleToggleMute = (channelId: string, muted: boolean) => {
    chat.setChannelMuted.mutate({ channelId, muted });
  };

  const handleAddMembers = (channelId: string, userIds: string[]) => {
    if (userIds.length === 0) return;
    chat.addChannelMembers.mutate({ channelId, userIds });
  };

  const handleRemoveMember = (channelId: string, userId: string) => {
    chat.removeChannelMember.mutate(
      { channelId, userId },
      {
        onSuccess: () => {
          // If the user removed themselves, the channel is no longer
          // in their list - close the modal so they're not stuck on a
          // stale view of a channel they're not in.
          if (me && userId === me.id) {
            setManageMembersChannelId(null);
            if (selectedId === channelId) setSelectedId(null);
          }
        },
      }
    );
  };

  return (
    <div className="flex h-[calc(100vh-9rem)] min-h-[34rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <ChannelSidebar
        me={me}
        usersById={usersById}
        teamChannels={teamChannels}
        dmChannels={dmChannels}
        selectedId={selectedId}
        onSelect={setSelectedId}
        search={search}
        onSearch={setSearch}
        unreadFor={unreadCount}
        totalUnread={totalUnread}
        onNewChannel={() => setShowNewChannel(true)}
        onNewDm={() => setShowNewDm(true)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {selected && me ? (
          <ChannelThread
            channel={selected}
            me={me}
            usersById={usersById}
            messages={messages}
            loading={messagesQuery.isLoading}
            hasMore={messagesQuery.hasMore}
            loadingOlder={messagesQuery.loadingOlder}
            onLoadOlder={messagesQuery.loadOlder}
            sending={chat.sendMessageWithAttachments.isLoading}
            typingUserIds={typingUserIds}
            replyTo={replyTo}
            onReply={setReplyTo}
            onCancelReply={() => setReplyTo(null)}
            onTyping={sendTypingSignal}
            onToggleMute={handleToggleMute}
            onManageMembers={() => setManageMembersChannelId(selected.id)}
            onSend={handleSend}
            onEdit={handleEdit}
            onReact={handleReact}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-gray-500">
            {chat.channels.isLoading
              ? "Loading conversations…"
              : "Select a conversation from the left to get started."}
          </div>
        )}
      </div>

      <NewChannelModal
        open={showNewChannel}
        onClose={() => setShowNewChannel(false)}
        users={users}
        meId={me?.id ?? null}
        creating={chat.createChannel.isLoading}
        onCreate={(input) => {
          chat.createChannel.mutate(input, {
            onSuccess: (res) => {
              const created = res?.data;
              if (created?.id) setSelectedId(created.id);
              setShowNewChannel(false);
            },
          });
        }}
      />

      <NewDmModal
        open={showNewDm}
        onClose={() => setShowNewDm(false)}
        users={users.filter((u) => !u.isMe)}
        starting={chat.startDm.isLoading}
        onSelect={(userId) => {
          chat.startDm.mutate(userId, {
            onSuccess: (res) => {
              const channel = res?.data;
              if (channel?.id) setSelectedId(channel.id);
              setShowNewDm(false);
            },
          });
        }}
      />

      <ManageMembersModal
        open={!!manageMembersChannel}
        channel={manageMembersChannel}
        me={me}
        users={users}
        busy={
          chat.addChannelMembers.isLoading ||
          chat.removeChannelMember.isLoading
        }
        onClose={() => setManageMembersChannelId(null)}
        onAdd={handleAddMembers}
        onRemove={handleRemoveMember}
      />
    </div>
  );
}
